import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Divider,
  Paper,
  Button,
  Skeleton,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { articleService, Article } from '../services/articleService';

const ArticleView = () => {
  const { slug } = useParams<{ slug: string }>();
  const theme = useTheme();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (slug) {
      fetchArticle(slug);
    }
  }, [slug]);

  const fetchArticle = async (articleSlug: string) => {
    try {
      setLoading(true);
      const data = await articleService.getArticleBySlug(articleSlug);
      setArticle(data);
      setError('');
    } catch (err) {
      setError('Failed to fetch article');
      console.error('Error fetching article:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'immigration-updates': theme.palette.info.main,
      'student-visa': theme.palette.success.main,
      'work-visa': theme.palette.warning.main,
      'family-visa': theme.palette.error.main,
    };
    return colors[category as keyof typeof colors] || theme.palette.primary.main;
  };

  const renderSkeleton = () => (
    <Box>
      <Skeleton variant="text" height={60} width="60%" sx={{ mb: 2 }} />
      <Skeleton variant="text" height={30} width="30%" sx={{ mb: 4 }} />
      <Skeleton variant="rectangular" height={400} sx={{ mb: 4 }} />
      <Skeleton variant="text" height={30} width="90%" />
      <Skeleton variant="text" height={30} width="95%" />
      <Skeleton variant="text" height={30} width="80%" sx={{ mb: 4 }} />
      
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ mb: 4 }}>
          <Skeleton variant="text" height={40} width="40%" sx={{ mb: 2 }} />
          <Skeleton variant="text" height={24} width="90%" />
          <Skeleton variant="text" height={24} width="85%" />
          <Skeleton variant="text" height={24} width="92%" />
        </Box>
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ mb: 4 }}>
            <Link component={RouterLink} to="/" color="inherit">
              Home
            </Link>
            <Link component={RouterLink} to="/blog" color="inherit">
              Blog
            </Link>
            <Typography color="text.primary">Article</Typography>
          </Breadcrumbs>
          {renderSkeleton()}
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 4 }}
          >
            Back to Blog
          </Button>
        </Container>
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Alert severity="warning" sx={{ mb: 4 }}>
            Article not found
          </Alert>
          <Button
            component={RouterLink}
            to="/blog"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 4 }}
          >
            Back to Blog
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: 'background.default' }}>
      <Container maxWidth="md">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link component={RouterLink} to="/" color="inherit">
            Home
          </Link>
          <Link component={RouterLink} to="/blog" color="inherit">
            Blog
          </Link>
          <Typography color="text.primary">
            {article.title}
          </Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          component={RouterLink}
          to="/blog"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Back to Blog
        </Button>

        {/* Article Header */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 800,
              lineHeight: 1.25,
              fontSize: { xs: '1.45rem', sm: '1.8rem', md: '2.4rem' },
              wordBreak: 'break-word',
            }}
          >
            {article.title}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.2, sm: 2 }, mb: 3 }}>
            <Chip
              label={article.category.split('-').join(' ').toUpperCase()}
              size="medium"
              sx={{
                bgcolor: `${getCategoryColor(article.category)}15`,
                color: getCategoryColor(article.category),
                fontWeight: 600,
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {new Date(article.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </Typography>
            </Box>

            {article.author && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {article.author}
                </Typography>
              </Box>
            )}
          </Box>

          {article.featuredImage && (
            <Box
              component="img"
              src={article.featuredImage.url}
              alt={article.title}
              sx={{
                width: '100%',
                height: { xs: 210, sm: 300, md: 420 },
                objectFit: 'cover',
                objectPosition: article.featuredImage.url?.includes('placehold.co')
                  ? 'center'
                  : 'top center',
                borderRadius: 2,
                mb: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            />
          )}

          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              lineHeight: 1.9,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              overflowWrap: 'anywhere',
            }}
          >
            {article.description}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Essay Style Article Body */}
        <Box>
          {article.sections.map((section, index) => (
            <Box key={index} sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 650,
                  mb: 2.25,
                  color: 'text.primary',
                  lineHeight: 1.35,
                  fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.6rem' },
                  wordBreak: 'break-word',
                }}
              >
                {section.title}
              </Typography>

              {section.details && section.details.length > 0 && section.details.map((detail, detailIndex) => (
                <Typography
                  key={detailIndex}
                  variant="body1"
                  sx={{
                    lineHeight: 1.95,
                    color: 'text.primary',
                    mb: 2.2,
                    fontSize: { xs: '1rem', md: '1.05rem' },
                    overflowWrap: 'anywhere',
                  }}
                >
                  {detail.text}
                </Typography>
              ))}

              {section.image && (
                <Box
                  component="img"
                  src={section.image.url}
                  alt={section.title}
                  sx={{
                    width: '100%',
                    borderRadius: 1.5,
                    mt: 2,
                    mb: 1,
                    height: 'auto',
                    maxHeight: 420,
                    objectFit: 'contain',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Call to Action */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mt: 5,
            borderRadius: 3,
            bgcolor: theme.palette.primary.light,
            backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.35rem', md: '1.6rem' } }}>
            Need Professional Immigration Advice?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Our expert immigration advisors can help you with your specific situation.
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              width: { xs: '100%', sm: 'auto' },
              borderRadius: 30,
              boxShadow: '0 4px 14px rgba(225, 122, 71, 0.4)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(225, 122, 71, 0.6)',
              },
            }}
          >
            Book Free Consultation
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default ArticleView; 